import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/ducks";
import { addToCart } from "../../store/ducks/app";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CompareModal: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const items = useSelector((state: RootState) => state.compare.items);
  const cart = useSelector((state: RootState) => state.store.cart);
  const selectedLocationStore = useSelector(
    (state: RootState) => state?.store?.selectedLocation
  );

  // Zoom modal
  const [zoomImage, setZoomImage] = React.useState<string | null>(null);

  if (!items || items.length === 0) return null;

  const isInCart = (id: string | number) =>
    cart.some((c) => c.id === id);

  const fields = [
    { label: "Brand", key: "Brand" },
    { label: "Model", key: "Model" },
    { label: "Tire Size", key: "TireSize" },
    { label: "Speed Rating", key: "speedRating" },
    { label: "Load Index", key: "loadIndex" },
    { label: "Price", key: "UnitPrice" },
    { label: "Warranty", key: "Warranty" },
    { label: "Sidewall", key: "SideWall" },
    { label: "UTQG", key: "Utqg" },
  ];

  const handleAddToCart = async (item: any) => {
    const cartItem = {
      locationCode: "",
      id: item.SystemId,
      no: item.No,
      name: item.Model ?? "Product",
      quantity: 1,
      image: item.Image_Source ?? "",
      description: item.Description ?? "",
      brand: item.Brand ?? "",
      price: item.UnitPrice ?? 0,
      showMessage:
        item[
          selectedLocationStore.locationCaption.charAt(0).toUpperCase() +
            selectedLocationStore.locationCaption.slice(1) +
            "Qty"
        ] == 0,
      services: item.optionalServices ?? [],
    };

    dispatch(addToCart(cartItem));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6">Compare Tires</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `180px repeat(${items.length}, minmax(160px, 1fr))`,
            columnGap: 3,
            rowGap: 1.5,
            minWidth: items.length >= 3 ? 900 : 700,
          }}
        >
          {/* Empty top-left cell */}
          <Box />

          {/* PRODUCT BOXES */}
          {items.map((item) => {
            const added = isInCart(item.SystemId);

            return (
              <Box
                key={item.SystemId}
                sx={{
                  textAlign: "center",
                  border: "1px solid #eee",
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: "#fafafa",
                }}
              >
                {/* CLICK TO ZOOM IMAGE */}
                <img
                  src={item.Image_Source}
                  onClick={() => setZoomImage(item.Image_Source)}
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "contain",
                    marginBottom: 8,
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                />

                <Typography fontWeight={700} fontSize="0.85rem" noWrap>
                  {item.Model}
                </Typography>

                <Typography
                  fontWeight={600}
                  color="green"
                  mb={1}
                  fontSize="0.8rem"
                >
                  ${item.UnitPrice?.toFixed(2)}
                </Typography>

                <Button
                  variant="contained"
                  size="small"
                  disabled={added}
                  onClick={() => handleAddToCart(item)}
                  sx={{
                    backgroundColor: added ? "gray" : "#E7131A",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: added ? "gray" : "#c51016",
                    },
                  }}
                >
                  {added ? "Added To Cart" : "Add to Cart"}
                </Button>
              </Box>
            );
          })}

          {/* DETAILS ROWS */}
          {fields.map((field) => (
            <React.Fragment key={field.key}>
              <Box sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                {field.label}
              </Box>

              {items.map((item) => (
                <Box key={item.SystemId + field.key}>
                  <Typography fontSize="0.8rem">
                    {item[field.key] ?? "—"}
                  </Typography>
                </Box>
              ))}
            </React.Fragment>
          ))}
        </Box>
      </DialogContent>

      {/* 🔍 IMAGE ZOOM MODAL */}
      <Dialog open={!!zoomImage} onClose={() => setZoomImage(null)} maxWidth="md" fullWidth>
        <Box sx={{ position: "relative", p: 2, textAlign: "center" }}>
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => setZoomImage(null)}
          >
            <CloseIcon />
          </IconButton>

          <img
            src={zoomImage!}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "80vh",
              objectFit: "contain",
            }}
          />
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default CompareModal;
